import Carousel from "@Components/Carousel";
import CarouselIndicator from "@Components/Carousel/components/CarouselIndicator";
import LazyImage from "@Components/Image";
import getImagePath from "@Utils/getImagePath";
import { useEffect, useState } from "react";
import styles from "./ImageSection.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import {
  getVideoSrc,
  getVideoThumb,
} from "@Pages/PropertyPage/components/SectionFeatured.tsx/utils";
import loadable from "@loadable/component";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";

const VideoPlayer = loadable(() => import("@Components/VideoPlayer"));
interface IImageSectionProps {
  images: string[];
  index: number;
  dataTestId?: string;
  videos: any[];
  isDesktop: boolean;
  noRedius?: boolean;
  className?: string;
  altData?: string;
  maxDots?: number;
  isGridView?: boolean;
  isMiniProgram?: boolean;
  showVideoFirst?: boolean;
}

const ImageSection = (props: IImageSectionProps) => {
  const {
    images,
    index,
    dataTestId,
    videos,
    isDesktop,
    noRedius,
    className,
    altData,
    maxDots,
    isGridView,
    isMiniProgram = false,
    showVideoFirst = false,
  } = props;
  const filteredVideos = videos.filter((video: any) => !video.path.includes("youtube")) || [];
  const virtualTourVideos = videos?.filter((video: any) => video.type === "vo_tour");
  const sortedVideos = virtualTourVideos?.sort(
    (v1: any, v2: any) => parseFloat(v2?.duration) - parseFloat(v1?.duration),
  );
  const videoItem = sortedVideos?.length ? sortedVideos[0] : filteredVideos[0];
  const [activePos, setActivePos] = useState(1);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const handleChangeSlide = (pos: number) => setActivePos(pos);

  const getMediasOrder = () => {
    if (!videoItem || images.length <= 1) return images;
    if (showVideoFirst) return [videoItem, ...images.slice(0, 4)];
    return [images[0], images[1], videoItem, ...images.slice(2, 4)];
  };
  const medias = getMediasOrder();

  const posterFor = (media: any) =>
    getVideoThumb(media, { height: isDesktop ? 230 : 215, width: 400 });

  const renderPoster = (media: any) => (
    <LazyImage
      src={posterFor(media)}
      width="100%"
      height="100%"
      rootMargin="0px 0px 0px 0px"
      alt={altData || "Property video"}
      className={styles.imgClass}
    />
  );

  const renderSlide = (media: any, slideIndex: number) => {
    if (media.path) {
      const isNativeFirstVideo = showVideoFirst && slideIndex === 0;

      if ((activePos >= 2 || isNativeFirstVideo) && (!isNativeFirstVideo || hasMounted)) {
        return (
          <VideoPlayer
            id={media?.path}
            name="virtual_video_fullscreen"
            isLazyLoaded={!isNativeFirstVideo}
            videoUrl={getVideoSrc(media, 400)}
            autoplay
            playsInline
            inLoop
            noControls
            muted
            posterUrl={posterFor(media)}
            isDesktop={isDesktop}
          />
        );
      }

      // The native first-result video is mounted client-side so SSR cannot start a second
      // media request before hydration. Keep its poster in the slot to avoid visual shift.
      return isNativeFirstVideo ? renderPoster(media) : null;
    }

    return (
      <LazyImage
        key={getImagePath(media, "gallery-image-small")}
        isEagerLoad={index <= 1 && slideIndex === 0 && !isMiniProgram}
        src={getImagePath(media, "gallery-image-small")}
        width="100%"
        height="100%"
        rootMargin="0px 0px 0px 0px"
        dataTestId={dataTestId}
        alt={altData || "main"}
        className={styles.imgClass}
      />
    );
  };

  const slides = medias.map(renderSlide);
  // The infinite loop repeats the first and last slide in its boundary slots. A second <video>
  // with the same src is a second download — browsers give each media element its own stream
  // rather than sharing one — so the clones get the poster instead. App-only: web keeps the
  // carousel's existing boundary behaviour.
  const cloneSlides = showVideoFirst
    ? medias.map((media: any, slideIndex: number) =>
        media?.path ? renderPoster(media) : slides[slideIndex],
      )
    : undefined;

  return (
    <div
      className={formatClassNames(
        isDesktop ? styles.desktopContainer : styles.container,
        !noRedius && styles.addRedius,
        className,
        isGridView && styles.gridView,
      )}
    >
      <Carousel
        slideList={slides}
        cloneSlideList={cloneSlides}
        activePos={activePos}
        showNavOnHover={isDesktop || isGridView}
        handleChangeSlide={handleChangeSlide}
        className={
          isDesktop
            ? styles.desktopContainer
            : formatClassNames(styles.container, isGridView && styles.gridView)
        }
      />
      <CarouselIndicator
        activeDot={activePos}
        totalSlider={medias.length}
        handleClickIndicator={handleChangeSlide}
        indicatorWrapperClassName={styles.indicator}
        type="line"
        activeDotColor="#fff"
        maxDots={maxDots}
      />
    </div>
  );
};

export default wrapperHOC(ImageSection, {
  componentName: "ImageSection-SearchListItemDesktop-SearchPage",
  showForChina: true,
});
